import React, { useState, useEffect } from 'react';
import { Table, Badge, Button, Alert } from 'react-bootstrap';
import ModalCorregirEquipos from './ModalCorregirEquipos';
import ModalEditarEquipo from './ModalEditarEquipo';

/**
 * ListaEquiposConfirmacion - Table showing added equipos with counter, edit and delete
 * @param {Object} props - Component props
 * @param {Array} props.equipos - Array of added equipos
 * @param {Function} props.onEliminar - Handler receiving equipo ID to remove
 * @param {Function} props.onEditar - Handler receiving equipo ID and updated data
 * @param {Array} props.listaSedes - Lista de sedes del cliente para el modal de corrección
 * @param {Function} props.onCorregir - Handler para corregir equipos con datos incompletos
 * @returns {JSX.Element}
 */

const ListaEquiposConfirmacion = ({ equipos = [], onEliminar, onEditar, listaSedes = [], onCorregir }) => {
   console.log('equipos en ListaEquiposConfirmacion: ', equipos)
   console.log('📋 ListaEquiposConfirmacion: listaSedes recibida:', listaSedes.length, 'sedes');
    
    // Estados para el modal de edición
    const [showModal, setShowModal] = useState(false);
    const [equipoEditando, setEquipoEditando] = useState(null);

    // Estados para el modal de corrección
    const [showModalCorregir, setShowModalCorregir] = useState(false);
    const [equiposProblematicos, setEquiposProblematicos] = useState([]);

    // DETECCIÓN CORRECTIVA: Detectar equipos sin sede cuando cambia el array
    useEffect(() => {
        const problematicos = equipos.filter(eq => !eq.sedeClient || !eq.sedeClient._id || !eq.sedeClient.nameSede);
        
        console.log('🔍 ListaEquiposConfirmacion: Detectando problemas...', problematicos.length, 'encontrados');
        console.log('🔍 Modal ya abierto?', showModalCorregir);
        
        // Solo abrir modal si hay problemas, hay sedes disponibles Y el modal NO está ya abierto
        if (problematicos.length > 0 && listaSedes.length > 0 && !showModalCorregir) {
            console.log('⚠️ ListaEquiposConfirmacion: Abriendo modal de corrección');
            setEquiposProblematicos(problematicos);
            setShowModalCorregir(true);
        }
    }, [equipos, listaSedes]);

    const count = equipos.length;
    const maxCount = 500;
    const isOverLimit = count > maxCount;

    // Abrir modal de edición
    const handleEditarClick = (equipo) => {
        console.log('✏️ Abriendo modal de edición para:', equipo._id);
        setEquipoEditando(equipo);
        setShowModal(true);
    };

    // Cerrar modal
    const handleCloseModal = () => {
        setShowModal(false);
        setEquipoEditando(null);
    };

    // Guardar cambios desde el modal de edición
    const handleGuardarEdicion = (equipoId, datosActualizados) => {
        console.log('💾 ListaEquiposConfirmacion: Guardando edición para', equipoId);
        onEditar(equipoId, datosActualizados);
    };

    return (
        <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Equipos Agregados</h5>
                <Badge 
                    bg={isOverLimit ? 'danger' : count > 450 ? 'warning' : 'primary'}
                    style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}
                >
                    {count} / {maxCount}
                </Badge>
            </div>

            {isOverLimit && (
                <Alert variant="danger">
                    <strong>¡Límite excedido!</strong> Se permiten máximo {maxCount} equipos por orden de trabajo.
                    Por favor, elimine {count - maxCount} equipo(s).
                </Alert>
            )}

            {count === 0 ? (
                <Alert variant="warning">
                    No hay equipos agregados. Por favor, agregue al menos un equipo usando las opciones anteriores.
                </Alert>
            ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table striped bordered hover>
                        <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                            <tr>
                                <th>#</th>
                                <th>Item</th>
                                <th>Marca</th>
                                <th>Modelo</th>
                                <th>Serie</th>
                                <th>Inventario</th>
                                <th>Ubicación</th>
                                <th>Sede</th>
                                <th>Puntos</th>
                                <th style={{ width: '150px' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {equipos.map((equipo, index) => (
                                <tr key={equipo._id || index}>
                                    <td>{index + 1}</td>
                                    <td>{equipo.item?.nombre || 'N/A'}</td>
                                    <td>{equipo.marca || 'No Presenta'}</td>
                                    <td>{equipo.modelo || 'No Presenta'}</td>
                                    <td>{equipo.serie || ''}</td>
                                    <td>{equipo.inventario || 'No Presenta'}</td>
                                    <td>{equipo.ubicacion || 'No Indicado'}</td>
                                    <td>{equipo.sedeClient?.nameSede || 'N/A'}</td>
                                    <td>{equipo.puntos || 'No Definidos'}</td>
                                    <td>
                                        <Button
                                            variant="warning"
                                            size="sm"
                                            onClick={() => handleEditarClick(equipo)}
                                            className="me-2"
                                        >
                                            <i className="bi bi-pencil-square"></i>
                                        </Button>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => onEliminar(equipo._id)}
                                        >
                                            <i className="bi bi-trash"></i>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* Modal de Edición */}
            <ModalEditarEquipo
                show={showModal}
                equipo={equipoEditando}
                listaSedes={listaSedes}
                onGuardar={handleGuardarEdicion}
                onCancelar={handleCloseModal}
            />

            {/* Modal para corregir equipos con datos incompletos */}
            <ModalCorregirEquipos
                show={showModalCorregir}
                equiposProblematicos={equiposProblematicos}
                listaSedes={listaSedes}
                onCorregir={onCorregir}
                onClose={() => {
                    console.log('✅ ListaEquiposConfirmacion: Modal cerrado después de corregir todos');
                    setShowModalCorregir(false);
                }}
                onCancelar={() => {
                    console.log('❌ ListaEquiposConfirmacion: Cancelando corrección, eliminando equipos problemáticos');
                    setShowModalCorregir(false);
                    // Eliminar equipos problemáticos de la lista
                    const idsProblematicos = equiposProblematicos.map(e => e._id);
                    idsProblematicos.forEach(id => onEliminar(id));
                }}
            />
        </div>
    );
};

export default ListaEquiposConfirmacion;
